import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import "./ApproveCandidates.css";
import axiosInstance from "../axios/axiosInstance";
import toast from "react-hot-toast";
import ScheduleInterviewDetailsModal from "./ScheduleInterviewDetailsModal";
import { X, Eye, Pencil } from "lucide-react";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";

import { renderAsync } from "docx-preview";

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
const ApproveIndividualCandidates = () => {
  const navigate = useNavigate();
  const [candidates, setCandidates] = useState([]);
  const [approvedCandidates, setApprovedCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [candidateDetails, setCandidateDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [viewMode, setViewMode] = useState("table");
  const [showEQSessionModal, setShowEQSessionModal] = useState(false);
  const [selectedCandidateForEQ, setSelectedCandidateForEQ] = useState(null);
  const [showPdfViewer, setShowPdfViewer] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [cvType, setCvType] = useState(null); // 'pdf' or 'docx'
  const [currentBlob, setCurrentBlob] = useState(null);
  const docxRef = useRef(null);

  useEffect(() => {
    fetchCandidates();
    fetchApprovedCandidates();
  }, []);

  useEffect(() => {
    if (showPdfViewer && cvType === "docx" && currentBlob && docxRef.current) {
      renderAsync(currentBlob, docxRef.current).catch((err) => {
        console.error("Error rendering DOCX:", err);
        toast.error("Failed to render DOCX preview");
      });
    }
  }, [showPdfViewer, cvType, currentBlob]);

  const fetchCandidates = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:3500"
        }/api/admin/candidates`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "true",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch candidates");
      }

      const data = await response.json();
      setCandidates(data);
    } catch (error) {
      setError("Failed to fetch candidates");
      console.error("Error fetching candidates:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApprovedCandidates = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:3500"
        }/api/admin/candidates/approved`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "true",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch approved candidates");
      }

      const data = await response.json();
      setApprovedCandidates(data);
    } catch (error) {
      console.error("Error fetching approved candidates:", error);
    }
  };

  const handleApprove = async (candidateId) => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:3500"
        }/api/admin/candidates/${candidateId}/approve`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "true",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to approve candidate");
      }

      // Update the candidate status in the local state
      setCandidates(
        candidates.map((candidate) =>
          candidate.id === candidateId
            ? { ...candidate, review_status: "Approved" }
            : candidate,
        ),
      );

      // Refresh the approved candidates list
      fetchApprovedCandidates();
    } catch (error) {
      setError("Failed to approve candidate");
      console.error("Error approving candidate:", error);
    }
  };

  const handleReject = async (candidateId) => {
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:3500"
        }/api/admin/candidates/${candidateId}/reject`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "true",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to reject candidate");
      }

      // Update the candidate status in the local state for both lists
      setCandidates(
        candidates.map((candidate) =>
          candidate.id === candidateId
            ? { ...candidate, review_status: "Rejected" }
            : candidate,
        ),
      );
      setApprovedCandidates(
        approvedCandidates.map((candidate) =>
          candidate.id === candidateId
            ? { ...candidate, review_status: "Rejected" }
            : candidate,
        ),
      );
    } catch (error) {
      setError("Failed to reject candidate");
      console.error("Error rejecting candidate:", error);
    }
  };

  const handleViewCV = async (userId, filename) => {
    try {
      console.log(
        "Starting CV view for userId:",
        userId,
        "filename:",
        filename,
      );
      setPdfLoading(true);
      setShowPdfViewer(true);
      setPageNumber(1);
      setCvType(null);

      const token = localStorage.getItem("adminToken");
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:3500";
      const fullUrl = `${apiUrl}/api/admin/candidates/${userId}/download-cv`;

      console.log("Fetching CV from:", fullUrl);

      const response = await fetch(fullUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        console.log("Blob received:", blob.size, "bytes, type:", blob.type);
        setCurrentBlob(blob);

        // ALWAYS trust filename extension over Content-Type (backend often returns wrong type)
        const filenameLower = filename?.toLowerCase() || "";
        console.log("Filename extension check:", filenameLower);

        if (filenameLower.endsWith(".docx")) {
          console.log("Detected DOCX file by extension, setting cvType='docx'");
          setCvType("docx");
        } else if (filenameLower.endsWith(".pdf")) {
          console.log("Detected PDF file by extension, setting cvType='pdf'");
          setCvType("pdf");
        } else if (blob.type.includes("word") || blob.type.includes("officedocument.wordprocessingml.document")) {
          console.log("Detected DOCX file by blob type, setting cvType='docx'");
          setCvType("docx");
        } else {
          console.log("No match found, defaulting to PDF, setting cvType='pdf'");
          setCvType("pdf"); // default
        }

        const url = window.URL.createObjectURL(blob);
        setPdfUrl(url);
      } else {
        const errorText = await response.text();
        console.error(
          "Failed to load CV. Status:",
          response.status,
          "Error:",
          errorText,
        );
        toast.error(`Failed to load CV: ${response.status}`);
        setShowPdfViewer(false);
      }
    } catch (error) {
      console.error("Error loading CV:", error);
      toast.error(`Error loading CV: ${error.message}`);
      setShowPdfViewer(false);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleClosePdfViewer = () => {
    setShowPdfViewer(false);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    setCurrentBlob(null);
    setCvType(null);
    setNumPages(null);
    setPageNumber(1);
  };

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfLoading(false);
  };

  const goToPrevPage = () => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber((prev) => Math.min(prev + 1, numPages));
  };

  const handleToggleActive = async (candidateId, currentIsActive) => {
    try {
      const token = localStorage.getItem("adminToken");

      const response = await axiosInstance.put(
        `/api/admin/candidates/${candidateId}/toggle-active`,
        { isActive: !currentIsActive },
      );

      console.log("Toggle response:", response.data);

      // Update the local state immediately for a responsive UI
      setApprovedCandidates(
        approvedCandidates.map((candidate) =>
          candidate.id === candidateId
            ? { ...candidate, isActive: !currentIsActive }
            : candidate,
        ),
      );

      // Optional success message
      // toast.success("Corporate status updated successfully");
    } catch (error) {
      console.log("Error toggling active status:", error.response?.data);
      const message =
        error.response?.data?.error || "Failed to toggle active status";
      // setError(message);
      toast.error(message);
      // toast.error(message);
    }
  };

  const cap = (str) => (str ? str.charAt(0).toUpperCase() + str.slice(1) : "");

  const handleViewDetails = async (candidate) => {
    try {
      setSelectedCandidate(candidate);
      setShowDetailsDialog(true);
      setDetailsLoading(true);
      setCandidateDetails(null);

      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:3500"
        }/api/admin/candidates/${candidate.user_id}/details`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "true",
          },
        },
      );

      if (!response.ok) {
        throw new Error("Failed to fetch candidate details");
      }

      const details = await response.json();
      setCandidateDetails(details);
    } catch (error) {
      setError("Failed to fetch candidate details");
      console.error("Error fetching candidate details:", error);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleCloseDetails = () => {
    setShowDetailsDialog(false);
    setSelectedCandidate(null);
    setCandidateDetails(null);
  };

  const handleScheduleEQSession = (candidate) => {
    setSelectedCandidateForEQ(candidate);
    setShowEQSessionModal(true);
  };

  const handleCloseEQSessionModal = () => {
    setShowEQSessionModal(false);
    setSelectedCandidateForEQ(null);
  };

  const handleSubmitEQSession = async (sessionData) => {
    try {
      const _sessionData = {
        ...sessionData,
        user_id: selectedCandidateForEQ.user_id,
      };
      // Check if we should update or add based on has_eq_schedule flag
      const isUpdate = selectedCandidateForEQ.has_eq_schedule;
      const response = isUpdate
        ? await axiosInstance.put(
          `/api/admin/candidates/candidate-eq-schedule`,
          _sessionData,
        )
        : await axiosInstance.post(
          `/api/admin/candidates/candidate-eq-schedule`,
          _sessionData,
        );

      if (response.status === 200) {
        // Update the local state to reflect the change
        setApprovedCandidates(
          approvedCandidates.map((candidate) =>
            candidate.id === selectedCandidateForEQ.id
              ? { ...candidate, has_eq_schedule: true }
              : candidate,
          ),
        );
        toast.success(
          isUpdate
            ? "Interview scheduled successfully!"
            : "Interview scheduled successfully!",
        );
      }
    } catch (error) {
      console.error("Error scheduling EQ session:", error);
      const message =
        error.response?.data?.error || "Failed to save interview details";
      toast.error(message);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="main-content">
        <div className="container">
          <div className="loading-spinner">Loading candidates...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="container">
        <div className="page-header">
          <Link to="/" className="back-link">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="m15 18-6-6 6-6" />
            </svg>
            Back to Dashboard
          </Link>
          <h1 className="page-title">Approve Individual Candidates</h1>
          <div className="view-toggle" role="tablist" aria-label="View mode">
            <button
              type="button"
              className={`toggle-btn ${viewMode === "table" ? "active" : ""}`}
              onClick={() => setViewMode("table")}
              role="tab"
              aria-selected={viewMode === "table"}
            >
              Table
            </button>
            <button
              type="button"
              className={`toggle-btn ${viewMode === "grid" ? "active" : ""}`}
              onClick={() => setViewMode("grid")}
              role="tab"
              aria-selected={viewMode === "grid"}
            >
              Grid
            </button>
          </div>
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* Under Review Candidates */}
        <div className="section-header">
          <h2>Under Review</h2>
          <p>Candidates waiting for approval</p>
        </div>

        {candidates.length === 0 ? (
          <div className="empty-state">
            <h3>No candidates found</h3>
            <p>
              There are currently no individual candidates waiting for approval.
            </p>
          </div>
        ) : (
          <>
            {viewMode === "table" ? (
              <div className="table-wrap">
                <table className="candidates-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Resume</th>
                      <th>LinkedIn</th>
                      <th>Decision</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.map((c) => (
                      <tr key={c.id}>
                        <td>
                          <div className="cell-user">
                            <div className="avatar-sm">
                              <span>{c.first_name?.[0] || "U"}</span>
                            </div>
                            <div className="cell-user-meta">
                              <div className="name">
                                {c.first_name} {c.last_name}
                              </div>
                              <div className="email">{c.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          {(() => {
                            const raw = (
                              c.review_status || "Under Review"
                            ).toString();
                            const cls = raw
                              .toLowerCase()
                              .replace(/\s+/g, "-")
                              .replace(/[^a-z-]/g, "");
                            return (
                              <span
                                className={`status-pill ${cls || "pending"}`}
                              >
                                {raw}
                              </span>
                            );
                          })()}
                        </td>
                        <td>
                          {new Date(c.created_at).toLocaleDateString()}{" "}
                          <span className="muted">
                            {new Date(c.created_at).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </td>

                        <td>
                          {c.cv_filename ? (
                            <button
                              className="link-action"
                              onClick={() =>
                                handleViewCV(c.user_id, c.cv_filename)
                              }
                            >
                              {c.cv_filename}
                            </button>
                          ) : (
                            <span className="muted">Not specified</span>
                          )}
                        </td>
                        <td>
                          {c.linkedin_url ? (
                            <a
                              href={
                                c.linkedin_url.startsWith("http")
                                  ? c.linkedin_url
                                  : `https://${c.linkedin_url}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {c.linkedin_url}
                            </a>
                          ) : (
                            <span className="not-specified">Not specified</span>
                          )}
                        </td>

                        <td>
                          <div className="decision-cell">
                            {(!c.review_status ||
                              c.review_status === "Under Review") && (
                                <>
                                  <button
                                    className="btn btn-success btn-sm"
                                    onClick={() => handleApprove(c.id)}
                                  >
                                    Approve
                                  </button>
                                  <button
                                    className="btn btn-danger btn-sm"
                                    onClick={() => handleReject(c.id)}
                                  >
                                    Reject
                                  </button>
                                </>
                              )}
                          </div>
                        </td>
                        <td>
                          <div className="profile-actions-cell">
                            <button
                              className="icon-btn icon-btn-view"
                              title="View Details"
                              onClick={() => handleViewDetails(c)}
                            >
                              <Eye size={18} />
                            </button>
                            {/* <button
                              className="icon-btn icon-btn-edit"
                              title="Edit Profile"
                              onClick={() => navigate(`/candidate-profile-edit/${c.user_id}`)}
                            >
                              <Pencil size={18} />
                            </button> */}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="candidates-grid">
                {candidates.map((candidate) => (
                  <div key={candidate.id} className="candidate-card">
                    <div className="candidate-header">
                      <div className="candidate-avatar">
                        {candidate.profile_picture ? (
                          <img src={candidate.profile_picture} alt="Profile" />
                        ) : (
                          <span>{candidate.first_name?.charAt(0) || "U"}</span>
                        )}
                      </div>
                      <div className="candidate-info">
                        <h3>
                          {candidate.first_name} {candidate.last_name}
                        </h3>
                        <p>{candidate.email}</p>
                      </div>
                      <div
                        className={`status-badge ${candidate.review_status?.toLowerCase() || "pending"
                          }`}
                      >
                        {candidate.review_status || "Pending"}
                      </div>
                    </div>
                    <div className="candidate-details">
                      <div className="detail-item">
                        <strong>Created:</strong>{" "}
                        {new Date(candidate.created_at).toLocaleDateString()}
                      </div>
                      <div className="detail-item">
                        <strong>Resume:</strong>
                        {candidate.cv_filename ? (
                          <button
                            className="download-link"
                            onClick={() =>
                              handleViewCV(
                                candidate.user_id,
                                candidate.cv_filename,
                              )
                            }
                          >
                            {candidate.cv_filename}
                          </button>
                        ) : (
                          <span className="not-specified">Not specified</span>
                        )}
                      </div>
                      <div className="detail-item">
                        <strong>LinkedIn:</strong>
                        {candidate.linkedin_url ? (
                          <a
                            href={
                              candidate.linkedin_url.startsWith("http")
                                ? candidate.linkedin_url
                                : `https://${candidate.linkedin_url}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {candidate.linkedin_url}
                          </a>
                        ) : (
                          <span className="not-specified">Not specified</span>
                        )}
                      </div>
                    </div>
                    <div className="grid-card-footer">
                      <div className="grid-footer-links">
                        <div className="profile-actions-cell">
                          <button
                            className="icon-btn icon-btn-view"
                            title="View Details"
                            onClick={() => handleViewDetails(candidate)}
                          >
                            <Eye size={18} /> View
                          </button>
                          {/* <button
                            className="icon-btn icon-btn-edit"
                            title="Edit Profile"
                            onClick={() => navigate(`/candidate-profile-edit/${candidate.user_id}`)}
                          >
                            <Pencil size={18} /> Edit
                          </button> */}
                        </div>
                      </div>
                      {(!candidate.review_status ||
                        candidate.review_status === "Under Review") && (
                          <div className="candidate-actions">
                            <button
                              className="btn btn-success btn-sm"
                              onClick={() => handleApprove(candidate.id)}
                            >
                              Approve
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleReject(candidate.id)}
                            >
                              Reject
                            </button>
                          </div>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Approved Candidates - Activate or Deactivate */}
        <div className="section-header">
          <h2>Activate or Deactivate</h2>
          <p>Approved candidates - manage their activation status</p>
        </div>

        {approvedCandidates.length === 0 ? (
          <div className="empty-state">
            <h3>No approved candidates found</h3>
            <p>There are currently no approved candidates to manage.</p>
          </div>
        ) : (
          <>
            {viewMode === "table" ? (
              <div className="table-wrap">
                <table className="candidates-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Active</th>
                      <th>Created</th>
                      <th>Resume</th>
                      <th>LinkedIn</th>
                      <th>Actions</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {approvedCandidates.map((c) => (
                      <tr key={c.id}>
                        <td>
                          <div className="cell-user">
                            <div className="avatar-sm">
                              <span>{c.first_name?.[0] || "U"}</span>
                            </div>
                            <div className="cell-user-meta">
                              <div className="name">
                                {c.first_name} {c.last_name}
                              </div>
                              <div className="email">{c.email}</div>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`status-pill ${c.isActive ? "approved" : "rejected"
                              }`}
                          >
                            {c.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          {new Date(c.created_at).toLocaleDateString()}{" "}
                          <span className="muted">
                            {new Date(c.created_at).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                        </td>
                        <td>
                          {c.cv_filename ? (
                            <button
                              className="link-action"
                              onClick={() =>
                                handleViewCV(c.user_id, c.cv_filename)
                              }
                            >
                              {c.cv_filename}
                            </button>
                          ) : (
                            <span className="muted">Not specified</span>
                          )}
                        </td>
                        <td>
                          {c.linkedin_url ? (
                            <a
                              href={
                                c.linkedin_url.startsWith("http")
                                  ? c.linkedin_url
                                  : `https://${c.linkedin_url}`
                              }
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {c.linkedin_url}
                            </a>
                          ) : (
                            <span className="not-specified">Not specified</span>
                          )}
                        </td>

                        <td>
                          <div className="decision-cell">
                            <button
                              className={`btn btn-sm ${c.isActive ? "btn-warning" : "btn-success"
                                }`}
                              onClick={() => handleToggleActive(c.id, c.isActive)}
                              disabled={c.review_status === "Rejected"}
                            >
                              {c.isActive ? "Deactivate" : "Activate"}
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleReject(c.id)}
                              disabled={c.review_status === "Rejected"}
                            >
                              Reject
                            </button>
                            {!c.has_eq_schedule && (
                              <button
                                className="btn btn-info btn-sm"
                                onClick={() => handleScheduleEQSession(c)}
                                disabled={c.review_status === "Rejected"}
                              >
                                {c.has_eq_schedule
                                  ? "Update Interview details"
                                  : "Add Interview details"}
                              </button>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="profile-actions-cell">
                            <button
                              className="icon-btn icon-btn-view"
                              title="View Details"
                              onClick={() => handleViewDetails(c)}
                            >
                              <Eye size={18} />
                            </button>
                            {/* <button
                              className="icon-btn icon-btn-edit"
                              title="Edit Profile"
                              onClick={() => navigate(`/candidate-profile-edit/${c.user_id}`)}
                            >
                              <Pencil size={18} />
                            </button> */}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="candidates-grid">
                {approvedCandidates.map((candidate) => (
                  <div key={candidate.id} className="candidate-card">
                    <div className="candidate-header">
                      <div className="candidate-avatar">
                        {candidate.profile_picture ? (
                          <img src={candidate.profile_picture} alt="Profile" />
                        ) : (
                          <span>{candidate.first_name?.charAt(0) || "U"}</span>
                        )}
                      </div>
                      <div className="candidate-info">
                        <h3>
                          {candidate.first_name} {candidate.last_name}
                        </h3>
                        <p>{candidate.email}</p>
                      </div>
                      <div
                        className={`status-badge ${candidate.review_status?.toLowerCase() || "pending"
                          }`}
                      >
                        {candidate.review_status || "Pending"}
                      </div>
                    </div>

                    <div className="candidate-details">
                      <div className="detail-item">
                        <strong>Created:</strong>{" "}
                        {new Date(candidate.created_at).toLocaleDateString()}
                      </div>
                      <div className="detail-item">
                        <strong>Active Status:</strong>
                        <span
                          className={`active-status ${candidate.isActive ? "active" : "inactive"
                            }`}
                        >
                          {candidate.isActive ? "Active" : "Inactive"}
                        </span>
                      </div>
                      <div className="detail-item">
                        <strong>Resume:</strong>
                        {candidate.cv_filename ? (
                          <button
                            className="download-link"
                            onClick={() =>
                              handleViewCV(
                                candidate.user_id,
                                candidate.cv_filename,
                              )
                            }
                          >
                            {candidate.cv_filename}
                          </button>
                        ) : (
                          <span className="not-specified">Not specified</span>
                        )}
                      </div>
                      <div className="detail-item">
                        <strong>LinkedIn:</strong>
                        {candidate.linkedin_url ? (
                          <a
                            href={
                              candidate.linkedin_url.startsWith("http")
                                ? candidate.linkedin_url
                                : `https://${candidate.linkedin_url}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {candidate.linkedin_url}
                          </a>
                        ) : (
                          <span className="not-specified">Not specified</span>
                        )}
                      </div>
                    </div>
                    <div className="grid-card-footer">
                      <div className="grid-footer-links">
                        <div className="profile-actions-cell">
                          <button
                            className="icon-btn icon-btn-view"
                            title="View Details"
                            onClick={() => handleViewDetails(candidate)}
                          >
                            <Eye size={18} /> View
                          </button>
                          {/* <button
                            className="icon-btn icon-btn-edit"
                            title="Edit Profile"
                            onClick={() => navigate(`/candidate-profile-edit/${candidate.user_id}`)}
                          >
                            <Pencil size={18} /> Edit
                          </button> */}
                        </div>
                      </div>
                      <div className="candidate-actions">
                        <button
                          className={`btn btn-sm ${candidate.isActive ? "btn-warning" : "btn-success"
                            }`}
                          onClick={() =>
                            handleToggleActive(candidate.id, candidate.isActive)
                          }
                          disabled={candidate.review_status === "Rejected"}
                        >
                          {candidate.isActive ? "Deactivate" : "Activate"}
                        </button>
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => handleReject(candidate.id)}
                          disabled={candidate.review_status === "Rejected"}
                        >
                          Reject
                        </button>
                        {!candidate.has_eq_schedule && (
                          <button
                            className="btn btn-info btn-sm"
                            onClick={() => handleScheduleEQSession(candidate)}
                            disabled={candidate.review_status === "Rejected"}
                          >
                            {candidate.has_eq_schedule
                              ? "Update Interview details"
                              : "Add Interview details"}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Candidate Details Dialog */}
        {showDetailsDialog && (
          <div className="dialog-overlay">
            <div className="dialog-content">
              <div className="dialog-header">
                <h2>Candidate Details</h2>
                <button className="modal-close" onClick={handleCloseDetails}>
                  <X size={24} />
                </button>
              </div>

              <div className="dialog-body">
                {detailsLoading ? (
                  <div className="loading">Loading candidate details...</div>
                ) : candidateDetails ? (
                  (() => {
                    const isApproved =
                      selectedCandidate?.review_status === "Approved" ||
                      candidateDetails?.review_status === "Approved";

                    return (
                      <div className="candidate-details-content">
                        <div className="detail-section">
                          <h3>Personal Information</h3>
                          {(candidateDetails.first_name || isApproved) && (
                            <div className="detail-row">
                              <strong>First Name:</strong>{" "}
                              {candidateDetails.first_name || "Not specified"}
                            </div>
                          )}
                          {(candidateDetails.last_name || isApproved) && (
                            <div className="detail-row">
                              <strong>Last Name:</strong>{" "}
                              {candidateDetails.last_name || "Not specified"}
                            </div>
                          )}
                          {(candidateDetails.email || isApproved) && (
                            <div className="detail-row">
                              <strong>Email:</strong>{" "}
                              {candidateDetails.email || "Not specified"}
                            </div>
                          )}
                          {((candidateDetails?.workEligibleSwiss &&
                            candidateDetails?.workEligibleSwiss?.length > 0) ||
                            isApproved) && (
                              <div className="detail-row">
                                <strong>Work eligibility for Switzerland:</strong>{" "}
                                {candidateDetails?.workEligibleSwiss &&
                                  candidateDetails?.workEligibleSwiss?.length > 0
                                  ? candidateDetails?.workEligibleSwiss[0]
                                  : "Not specified"}
                              </div>
                            )}
                          {(candidateDetails.job_search_status || isApproved) && (
                            <div className="detail-row">
                              <strong>Job Search Status:</strong>{" "}
                              {candidateDetails.job_search_status || "Not specified"}
                            </div>
                          )}
                          {(candidateDetails.current_position || isApproved) && (
                            <div className="detail-row">
                              <strong>Current Position:</strong>{" "}
                              {candidateDetails.current_position || "Not specified"}
                            </div>
                          )}

                          {(() => {
                            const hasNotice =
                              (candidateDetails?.notice_period_value === 0 ||
                                candidateDetails?.notice_period_value) &&
                              candidateDetails.notice_period_unit;
                            return hasNotice || isApproved ? (
                              <div className="detail-row">
                                <strong>Notice period:</strong>{" "}
                                {candidateDetails.notice_period_value === 0
                                  ? "Available immediately"
                                  : candidateDetails.notice_period_value &&
                                    candidateDetails.notice_period_unit
                                    ? `${candidateDetails.notice_period_value} ${cap(candidateDetails.notice_period_unit) || ""}`
                                    : "Not specified"}
                              </div>
                            ) : null;
                          })()}
                          {(candidateDetails.linkedin_url || isApproved) && (
                            <div className="detail-row">
                              <strong>LinkedIn URL:</strong>{" "}
                              {candidateDetails.linkedin_url ? (
                                <a
                                  href={
                                    candidateDetails.linkedin_url.startsWith("http")
                                      ? candidateDetails.linkedin_url
                                      : `https://${candidateDetails.linkedin_url}`
                                  }
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {candidateDetails.linkedin_url}
                                </a>
                              ) : (
                                "Not specified"
                              )}
                            </div>
                          )}
                          {(candidateDetails.cv_filename || isApproved) && (
                            <div className="detail-row">
                              <strong>Cv:</strong>{" "}
                              {candidateDetails.cv_filename ? (
                                <button
                                  className="download-link"
                                  onClick={() =>
                                    handleViewCV(
                                      candidateDetails.user_id,
                                      candidateDetails.cv_filename,
                                    )
                                  }
                                >
                                  {candidateDetails.cv_filename}
                                </button>
                              ) : (
                                "Not specified"
                              )}
                            </div>
                          )}
                          {(() => {
                            const val = Array.isArray(candidateDetails.employment_type)
                              ? candidateDetails.employment_type.join(", ")
                              : candidateDetails.employment_type;
                            return val || isApproved ? (
                              <div className="detail-row">
                                <strong>Employment Type:</strong>{" "}
                                {val || "Not specified"}
                              </div>
                            ) : null;
                          })()}
                          {(() => {
                            const val = Array.isArray(candidateDetails.workSetup)
                              ? candidateDetails.workSetup.join(", ")
                              : candidateDetails.workSetup;
                            return val || isApproved ? (
                              <div className="detail-row">
                                <strong>Work Setup:</strong>{" "}
                                {val || "Not specified"}
                              </div>
                            ) : null;
                          })()}
                          {(candidateDetails.willRelocate || isApproved) && (
                            <div className="detail-row">
                              <strong>Will Relocate:</strong>{" "}
                              {candidateDetails.willRelocate || "Not specified"}
                            </div>
                          )}
                        </div>

                        {((candidateDetails.workEligibleCountries &&
                          candidateDetails.workEligibleCountries.length > 0) ||
                          isApproved) && (
                            <div className="detail-section">
                              <h3>Work Eligibility</h3>
                              <div className="detail-row">
                                <strong>Work Eligible Countries:</strong>{" "}
                                {candidateDetails.workEligibleCountries &&
                                  candidateDetails.workEligibleCountries.length > 0
                                  ? candidateDetails.workEligibleCountries.join(", ")
                                  : "Not specified"}
                              </div>
                            </div>
                          )}

                        {((candidateDetails.preferredRoles &&
                          candidateDetails.preferredRoles.length > 0) ||
                          isApproved) && (
                            <div className="detail-section">
                              <h3>Preferred Roles</h3>
                              {candidateDetails.preferredRoles &&
                                candidateDetails.preferredRoles.length > 0 ? (
                                candidateDetails.preferredRoles.map(
                                  (role, index) => (
                                    <div key={index} className="role-item">
                                      {(role.function_name || isApproved) && (
                                        <div className="detail-row">
                                          <strong>Function:</strong>{" "}
                                          {role.function_name || "Not specified"}
                                        </div>
                                      )}
                                      {(role.role_name || isApproved) && (
                                        <div className="detail-row">
                                          <strong>Role:</strong>{" "}
                                          {role.role_name || "Not specified"}
                                        </div>
                                      )}

                                      {((role.skills && role.skills.length > 0) ||
                                        isApproved) && (
                                          <div className="skills-list">
                                            <div className="detail-row">
                                              <strong>Skills:</strong>
                                            </div>
                                            {role.skills && role.skills.length > 0 ? (
                                              role.skills.map(
                                                (skillObj, skillIndex) => {
                                                  const skillName =
                                                    Object.keys(skillObj)[0];
                                                  const skillData =
                                                    skillObj[skillName];

                                                  const hasValidSubSkills =
                                                    skillData.subSkills &&
                                                    skillData.subSkills.length > 0 &&
                                                    !(
                                                      skillData.subSkills.length ===
                                                      1 &&
                                                      (typeof skillData.subSkills[0] ===
                                                        "string"
                                                        ? skillData.subSkills[0].trim() ===
                                                        skillName
                                                        : skillData.subSkills[0].name.trim() ===
                                                        skillName)
                                                    );

                                                  return (
                                                    <div
                                                      key={skillIndex}
                                                      className="skill-item"
                                                      style={{
                                                        marginLeft: "1rem",
                                                        marginBottom: "0.5rem",
                                                      }}
                                                    >
                                                      <div className="detail-row">
                                                        {skillName}{" "}
                                                        {!hasValidSubSkills && (
                                                          <em>{skillData.level}</em>
                                                        )}
                                                      </div>
                                                      {hasValidSubSkills && (
                                                        <div
                                                          className="subskills-row"
                                                          style={{
                                                            marginLeft: "1.5rem",
                                                            color: "#555",
                                                            fontSize: "0.9rem",
                                                          }}
                                                        >
                                                          {skillData.subSkills.map(
                                                            (sub, i) => (
                                                              <span key={i}>
                                                                {typeof sub ===
                                                                  "string"
                                                                  ? sub
                                                                  : `${sub.name} (${sub.level})`}
                                                                {i <
                                                                  skillData.subSkills
                                                                    .length -
                                                                  1
                                                                  ? ", "
                                                                  : ""}
                                                              </span>
                                                            ),
                                                          )}
                                                        </div>
                                                      )}
                                                    </div>
                                                  );
                                                },
                                              )
                                            ) : (
                                              <div
                                                className="detail-row"
                                                style={{ marginLeft: "1rem" }}
                                              >
                                                No skills listed
                                              </div>
                                            )}
                                          </div>
                                        )}
                                      {index <
                                        candidateDetails.preferredRoles.length -
                                        1 && <hr />}
                                    </div>
                                  ),
                                )
                              ) : (
                                <div className="detail-row">
                                  No preferred roles specified
                                </div>
                              )}
                            </div>
                          )}

                        {((candidateDetails.softSkills &&
                          candidateDetails.softSkills.length > 0) ||
                          isApproved) && (
                            <div className="detail-section">
                              <h3>Soft Skills</h3>
                              {candidateDetails.softSkills &&
                                candidateDetails.softSkills.length > 0 ? (
                                candidateDetails.softSkills.map((skill, index) => (
                                  <div key={index} className="detail-row">
                                    <strong>{skill.name}:</strong>{" "}
                                    {skill.evidence || "No evidence provided"}
                                  </div>
                                ))
                              ) : (
                                <div className="detail-row">
                                  No soft skills listed
                                </div>
                              )}
                            </div>
                          )}

                        {((candidateDetails.educations &&
                          candidateDetails.educations.length > 0) ||
                          isApproved) && (
                            <div className="detail-section">
                              <h3>Education</h3>
                              {candidateDetails.educations &&
                                candidateDetails.educations.length > 0 ? (
                                candidateDetails.educations.map((edu, index) => (
                                  <div key={index} className="education-item">
                                    {(edu.degree || isApproved) && (
                                      <div className="detail-row">
                                        <strong>Degree:</strong>{" "}
                                        {edu.degree || "Not specified"}
                                      </div>
                                    )}
                                    {(edu.field_of_study || isApproved) && (
                                      <div className="detail-row">
                                        <strong>Field of Study:</strong>{" "}
                                        {edu.field_of_study || "Not specified"}
                                      </div>
                                    )}
                                    {index <
                                      candidateDetails.educations.length - 1 && (
                                        <hr />
                                      )}
                                  </div>
                                ))
                              ) : (
                                <div className="detail-row">
                                  No education details listed
                                </div>
                              )}
                            </div>
                          )}

                        {((candidateDetails.certifications &&
                          candidateDetails.certifications.length > 0) ||
                          isApproved) && (
                            <div className="detail-section">
                              <h3>Certifications</h3>
                              {candidateDetails.certifications &&
                                candidateDetails.certifications.length > 0 ? (
                                candidateDetails.certifications.map(
                                  (cert, index) => (
                                    <div key={index} className="cert-item">
                                      {(cert.certification_name || isApproved) && (
                                        <div className="detail-row">
                                          <strong>Certification:</strong>{" "}
                                          {cert.certification_name ||
                                            "Not specified"}
                                        </div>
                                      )}
                                      {(cert.issuing_organization ||
                                        isApproved) && (
                                          <div className="detail-row">
                                            <strong>Organization:</strong>{" "}
                                            {cert.issuing_organization ||
                                              "Not specified"}
                                          </div>
                                        )}
                                      {(cert.issue_date || isApproved) && (
                                        <div className="detail-row">
                                          <strong>Issue Date:</strong>{" "}
                                          {cert.issue_date || "Not specified"}
                                        </div>
                                      )}
                                      {(cert.no_expiry ||
                                        cert.expiry_date ||
                                        isApproved) && (
                                          <div className="detail-row">
                                            <strong>Expiry:</strong>{" "}
                                            {cert.no_expiry
                                              ? "No Expiry"
                                              : cert.expiry_date
                                                ? cert.expiry_date
                                                : "Not specified"}
                                          </div>
                                        )}
                                      {(cert.credential_id || isApproved) && (
                                        <div className="detail-row">
                                          <strong>Credential ID:</strong>{" "}
                                          {cert.credential_id || "Not specified"}
                                        </div>
                                      )}
                                      {cert.credential_url && (
                                        <div className="detail-row">
                                          <strong>Credential URL:</strong>{" "}
                                          <a
                                            href={cert.credential_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                          >
                                            {cert.credential_url}
                                          </a>
                                        </div>
                                      )}
                                      {index <
                                        candidateDetails.certifications.length -
                                        1 && <hr />}
                                    </div>
                                  ),
                                )
                              ) : (
                                <div className="detail-row">
                                  No certifications listed
                                </div>
                              )}
                            </div>
                          )}
                      </div>
                    );
                  })()
                ) : (
                  <div className="error">Failed to load candidate details</div>
                )}
              </div>

              <div className="dialog-footer">
                <button
                  className="btn btn-primary"
                  onClick={handleCloseDetails}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Schedule Interview Modal */}
        <ScheduleInterviewDetailsModal
          isOpen={showEQSessionModal}
          onClose={handleCloseEQSessionModal}
          candidate={selectedCandidateForEQ}
          onSubmit={handleSubmitEQSession}
          isUpdate={selectedCandidateForEQ?.has_eq_schedule || false}
        />

        {/* PDF Viewer Modal */}
        {showPdfViewer && (
          <div className="dialog-overlay" style={{ zIndex: 1000 }}>
            <div
              className="dialog-content"
              style={{ maxWidth: "90vw", width: "900px", maxHeight: "90vh" }}
            >
              <div className="dialog-header">
                <h2>Candidate CV</h2>
                <button className="modal-close" onClick={handleClosePdfViewer}>
                  <X size={24} />
                </button>
              </div>

              <div
                className="dialog-body"
                style={{
                  padding: "1rem",
                  maxHeight: "calc(90vh - 150px)",
                  overflow: "auto",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                {pdfLoading ? (
                  <div className="loading">Loading CV...</div>
                ) : cvType === "docx" && currentBlob ? (
                  <>
                    <div
                      ref={docxRef}
                      style={{
                        width: "100%",
                        minHeight: "500px",
                        backgroundColor: "#fff",
                      }}
                    />
                  </>
                ) : cvType === "pdf" && pdfUrl ? (
                  <>
                    <Document
                      file={pdfUrl}
                      onLoadSuccess={onDocumentLoadSuccess}
                      onLoadError={(error) => {
                        console.error("Error loading PDF:", error);
                        toast.error("Failed to load PDF");
                      }}
                      loading={<div className="loading">Loading PDF...</div>}
                    >
                      <Page
                        pageNumber={pageNumber}
                        renderTextLayer={false}
                        renderAnnotationLayer={false}
                        width={Math.min(850, window.innerWidth * 0.85)}
                      />
                    </Document>

                    {numPages && numPages > 1 && (
                      <div
                        style={{
                          marginTop: "1rem",
                          display: "flex",
                          alignItems: "center",
                          gap: "1rem",
                          padding: "0.5rem",
                          background: "#f5f5f5",
                          borderRadius: "4px",
                        }}
                      >
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={goToPrevPage}
                          disabled={pageNumber <= 1}
                          style={{ minWidth: "80px" }}
                        >
                          Previous
                        </button>
                        <span style={{ fontWeight: "500" }}>
                          Page {pageNumber} of {numPages}
                        </span>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={goToNextPage}
                          disabled={pageNumber >= numPages}
                          style={{ minWidth: "80px" }}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </>
                ) : cvType === "docx" ? (
                  <div
                    ref={docxRef}
                    style={{
                      width: "100%",
                      minHeight: "500px",
                      backgroundColor: "#fff",
                    }}
                  />
                ) : (
                  <div className="error">Failed to load CV</div>
                )}
              </div>

              <div className="dialog-footer">
                <button
                  className="btn btn-primary"
                  onClick={handleClosePdfViewer}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApproveIndividualCandidates;
